import React, { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Dialog, Transition } from '@headlessui/react';
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import {
  HiOutlineBuildingOffice2,
  HiPlus,
  HiOutlineTrash,
  HiMagnifyingGlass,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineCube
} from "react-icons/hi2";
import { useToast } from "../../components/ui/ToastContext";
import { jwtDecode } from 'jwt-decode';
import { API_URL } from "../../config";
import { translateApiError } from "../../api/client";

function DashboardCard07Prod() {
  const { showToast } = useToast();
  const location = useLocation();
  const { item: initialData } = location.state || {};
  const [selectedEditRowData, setSelectedEditRowData] = useState(initialData);

  const [prod, setProd] = useState([]);
  const [manageprod, setManageProd] = useState([]);
  const [Rep, setRep] = useState([]);
  const [selectedProducers, setSelectedProducers] = useState([]);
  const [selectedProducerGroups, setSelectedProducerGroups] = useState([]);
  const [initialAssignedProducers, setInitialAssignedProducers] = useState([]);
  const [initialAssignedGroups, setInitialAssignedGroups] = useState([]);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalFilterType, setModalFilterType] = useState('all'); // 'all', 'selected', 'unselected'
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectpg, setIsselectpg] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [showGrid, setShowGrid] = useState(true);

  const token = localStorage.getItem('jwtToken');

  const getTargetUserId = () => {
    if (selectedEditRowData?.user_id) return Number(selectedEditRowData.user_id);
    if (selectedEditRowData?.id) return Number(selectedEditRowData.id);
    if (initialData?.user_id) return Number(initialData.user_id);
    if (initialData?.id) return Number(initialData.id);
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return Number(decoded?.userId || decoded?.user_id);
      } catch (e) {
        console.error(e);
      }
    }
    return null;
  };

  const userRole = (user?.role || user?.role_name || "").toLowerCase().trim();
  const isAdmin = userRole === "admin" || user?.role === "Admin" || user?.role_id === 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGrid(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUser(decoded);
      } catch (error) {
        console.error('Error decoding the token:', error);
      }
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const targetUserId = getTargetUserId();
      if (!targetUserId) {
        setManageProd([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/usersproducersacces`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: { user_id: targetUserId },
      });

      const userAccess = Array.isArray(response.data)
        ? response.data.filter((i) => Number(i.user_id) === Number(targetUserId))
        : [];

      if (userAccess.length === 0) {
        setManageProd([]);
        return;
      }

      // Check if backend already populated producer_name / producer_group_name
      let formatted = userAccess.map((item) => ({
        id: Number(item.producer_or_producer_group_id),
        producer_name: item.producer_name || item.name || "",
        producer_group_name: item.producer_group_name || item.name || "",
        type_id: Number(item.relation_type_id),
      }));

      // If any names are missing, query producers and producer groups to resolve them
      const hasMissingNames = formatted.some(
        (f) => (f.type_id === 1 && !f.producer_name) || (f.type_id === 2 && !f.producer_group_name)
      );

      if (hasMissingNames) {
        const [resProducers, resGroups] = await Promise.allSettled([
          axios.get(`${API_URL}/api/producer`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/producer_group`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const producersList = resProducers.status === 'fulfilled' && Array.isArray(resProducers.value?.data)
          ? resProducers.value.data
          : [];
        const groupsList = resGroups.status === 'fulfilled' && Array.isArray(resGroups.value?.data)
          ? resGroups.value.data
          : [];

        const pMap = new Map(producersList.map((p) => [Number(p.producer_id), p.producer_name]));
        const gMap = new Map(groupsList.map((g) => [Number(g.producer_group_id), g.producer_group_name]));

        formatted = formatted.map((f) => ({
          ...f,
          producer_name: f.type_id === 1 ? (pMap.get(f.id) || f.producer_name || `Producător #${f.id}`) : f.producer_name,
          producer_group_name: f.type_id === 2 ? (gMap.get(f.id) || f.producer_group_name || `Grup #${f.id}`) : f.producer_group_name,
        }));
      }

      setManageProd(formatted);
    } catch (error) {
      console.error("Error in fetchData:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedEditRowData, initialData]);

  const handleOpenProd = async () => {
    setOpen(true);
    setModalSearchQuery('');
    setModalFilterType('all');

    const currentP = manageprod.filter((m) => m.type_id === 1).map((m) => Number(m.id));
    const currentG = manageprod.filter((m) => m.type_id === 2).map((m) => Number(m.id));
    setSelectedProducers(currentP);
    setSelectedProducerGroups(currentG);
    setInitialAssignedProducers(currentP);
    setInitialAssignedGroups(currentG);

    try {
      const [resProducers, resGroups] = await Promise.allSettled([
        axios.get(`${API_URL}/api/producer`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        axios.get(`${API_URL}/api/producer_group`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
      ]);

      if (resProducers.status === 'fulfilled' && Array.isArray(resProducers.value?.data)) {
        setProd(resProducers.value.data);
      }
      if (resGroups.status === 'fulfilled' && Array.isArray(resGroups.value?.data)) {
        setRep(resGroups.value.data);
      }
    } catch (error) {
      console.error("Error fetching producers for modal:", error);
    }
  };

  const handleToggleprod = (id, type) => {
    const numId = Number(id);
    if (type === 'producer') {
      setSelectedProducers((prev) =>
        prev.includes(numId) ? prev.filter((item) => item !== numId) : [...prev, numId]
      );
    } else if (type === 'group') {
      setSelectedProducerGroups((prev) =>
        prev.includes(numId) ? prev.filter((item) => item !== numId) : [...prev, numId]
      );
    }
  };

  const toAddProducers = selectedProducers.filter((id) => !initialAssignedProducers.includes(id));
  const toDeleteProducers = initialAssignedProducers.filter((id) => !selectedProducers.includes(id));
  const toAddGroups = selectedProducerGroups.filter((id) => !initialAssignedGroups.includes(id));
  const toDeleteGroups = initialAssignedGroups.filter((id) => !selectedProducerGroups.includes(id));

  const hasModalChanges =
    toAddProducers.length > 0 ||
    toDeleteProducers.length > 0 ||
    toAddGroups.length > 0 ||
    toDeleteGroups.length > 0;

  const handleSave = async () => {
    const targetUserId = getTargetUserId();
    if (!targetUserId) return;
    if (!hasModalChanges) {
      setOpen(false);
      return;
    }

    try {
      // 1. Inserare producatori / grupuri noi
      const producerData = toAddProducers.map((id) => ({
        user_id: targetUserId,
        producer_or_producer_group_id: id,
        relation_type_id: 1,
      }));

      const groupData = toAddGroups.map((id) => ({
        user_id: targetUserId,
        producer_or_producer_group_id: id,
        relation_type_id: 2,
      }));

      const payloadAdd = [...producerData, ...groupData];
      if (payloadAdd.length > 0) {
        await axios.post(`${API_URL}/api/usersproducersacces`, payloadAdd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      // 2. Stergere producatori deselectati
      for (const id of toDeleteProducers) {
        await axios.delete(`${API_URL}/api/usersproducersacces/${targetUserId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: {
            producer_or_producer_group_id: id,
            relation_type_id: 1,
          },
          params: {
            producer_or_producer_group_id: id,
            relation_type_id: 1,
          },
        });
      }

      // 3. Stergere grupuri deselectate
      for (const id of toDeleteGroups) {
        await axios.delete(`${API_URL}/api/usersproducersacces/${targetUserId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: {
            producer_or_producer_group_id: id,
            relation_type_id: 2,
          },
          params: {
            producer_or_producer_group_id: id,
            relation_type_id: 2,
          },
        });
      }

      setOpen(false);
      await fetchData();
      showToast({
        message: "Accesul la producători a fost salvat cu succes!",
        type: "success",
        icon: <FaRegCheckCircle className="text-emerald-600 dark:text-emerald-500" />,
      });
    } catch (error) {
      console.error("Error saving producer access:", error);
      showToast({
        message: translateApiError(error),
        type: "error",
        icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
      });
    }
  };

  const handleDelete = async (item) => {
    const targetUserId = getTargetUserId();
    if (!targetUserId) return;
    const itemKey = `${item.id}-${item.type_id}`;
    setDeletingKey(itemKey);

    try {
      await axios.delete(`${API_URL}/api/usersproducersacces/${targetUserId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          producer_or_producer_group_id: item.id,
          relation_type_id: item.type_id,
        },
        params: {
          producer_or_producer_group_id: item.id,
          relation_type_id: item.type_id,
        },
      });

      // Optimistic update
      setManageProd((prev) => prev.filter((p) => !(p.id === item.id && p.type_id === item.type_id)));

      showToast({
        message: "Accesul a fost șters!",
        type: "success",
        icon: <FaRegCheckCircle className="text-emerald-600 dark:text-emerald-500" />,
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting producer access:", error);
      showToast({
        message: translateApiError(error),
        type: "error",
        icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
      });
    } finally {
      setDeletingKey(null);
    }
  };

  const handleButtonClick = async () => {
    setIsLoading(true);
    try {
      await handleSave();
    } finally {
      setIsLoading(false);
    }
  };

  const filteredManageProd = manageprod.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const name = item.type_id === 1 ? item.producer_name : item.producer_group_name;
    return name?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filteredManageProd.length / itemsPerPage) || 1;
  const currentItems = filteredManageProd.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-full">
      {showGrid ? (
        <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-[0_4px_14px_rgba(22,31,50,0.04)] animate-pulse">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-44 mb-4"></div>
          <div className="h-44 bg-gray-100 dark:bg-gray-700/50 rounded-xl"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl shadow-[0_4px_14px_rgba(22,31,50,0.04)] overflow-hidden transition-all duration-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-[#F1F5F9] dark:border-gray-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3.5">
            {/* Header Left: Icon, Title & Subtitle */}
            <div className="flex items-center gap-3">
              <div className="w-[34px] h-[34px] rounded-[10px] bg-[#F1EEFF] dark:bg-indigo-950/60 text-[#6755E8] dark:text-indigo-400 flex items-center justify-center shrink-0">
                <HiOutlineBuildingOffice2 className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 leading-tight">
                  Acces producători
                </h3>
                <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5">
                  Gestionează accesul la producători și grupuri de producători.
                </p>
              </div>
            </div>

            {/* Header Right: Search Field & CTA */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5">
              <div className="relative w-full sm:w-[220px]">
                <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
                <input
                  type="text"
                  className="w-full h-[34px] pl-8 pr-3 rounded-lg border border-[#E2E8F0] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:border-[#6755E8] focus:outline-none focus:ring-2 focus:ring-[#6755E8]/10 transition-all"
                  placeholder="Caută producător..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Caută producător"
                />
              </div>

              {isAdmin && (
                <button
                  type="button"
                  onClick={handleOpenProd}
                  className="h-[34px] px-3.5 rounded-lg bg-[#6755E8] hover:bg-[#5844E3] text-white text-[11px] font-medium inline-flex items-center justify-center gap-1.5 shadow-xs transition-all duration-150 hover:-translate-y-0.5 cursor-pointer shrink-0"
                >
                  <HiPlus className="w-3.5 h-3.5" />
                  <span>Adaugă producător</span>
                </button>
              )}
            </div>
          </div>

          {/* Modal Adăugare / Modificare Producători */}
          <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-2xl transition-all border border-[#E7EAF0] dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <Dialog.Title as="h3" className="text-base font-bold text-[#1E293B] dark:text-gray-100">
                          Gestionează accesul la producători
                        </Dialog.Title>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Total selectați: <strong className="text-[#6755E8] dark:text-indigo-400">{selectedProducers.length + selectedProducerGroups.length}</strong>
                        </span>
                      </div>

                      <div className="mt-4 relative">
                        <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          className="w-full h-9 pl-9 pr-3.5 rounded-lg border border-[#D9DEE8] dark:border-gray-600 bg-[#F8F9FD] dark:bg-gray-700/50 text-xs text-[#1E293B] dark:text-gray-100 placeholder-[#A3AABA] focus:border-[#6755E8] focus:bg-white focus:outline-none"
                          placeholder="Caută în listă..."
                          value={modalSearchQuery}
                          onChange={(e) => setModalSearchQuery(e.target.value)}
                        />
                      </div>

                      {/* Switch Tab Producer vs Group */}
                      <div className="mt-3 p-1 bg-[#F1F3F7] dark:bg-gray-700/60 rounded-xl grid grid-cols-2 gap-1 text-xs font-semibold">
                        <button
                          type="button"
                          className={`py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                            selectpg
                              ? "bg-white dark:bg-gray-800 text-[#1E293B] dark:text-white shadow-xs"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-900"
                          }`}
                          onClick={() => setIsselectpg(true)}
                        >
                          <span>Producători</span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 font-bold">
                            {selectedProducers.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                            !selectpg
                              ? "bg-white dark:bg-gray-800 text-[#1E293B] dark:text-white shadow-xs"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-900"
                          }`}
                          onClick={() => setIsselectpg(false)}
                        >
                          <span>Grup producători</span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 font-bold">
                            {selectedProducerGroups.length}
                          </span>
                        </button>
                      </div>

                      {/* Filter sub-tabs & Select All / Deselect All */}
                      <div className="mt-2.5 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 px-1">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setModalFilterType('all')}
                            className={`px-2 py-0.5 rounded cursor-pointer ${
                              modalFilterType === 'all' ? 'bg-[#1E293B] text-white font-semibold' : 'hover:text-gray-900'
                            }`}
                          >
                            Toate
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalFilterType('selected')}
                            className={`px-2 py-0.5 rounded cursor-pointer ${
                              modalFilterType === 'selected' ? 'bg-[#6755E8] text-white font-semibold' : 'hover:text-gray-900'
                            }`}
                          >
                            Selectate
                          </button>
                          <button
                            type="button"
                            onClick={() => setModalFilterType('unselected')}
                            className={`px-2 py-0.5 rounded cursor-pointer ${
                              modalFilterType === 'unselected' ? 'bg-gray-600 text-white font-semibold' : 'hover:text-gray-900'
                            }`}
                          >
                            Neselectate
                          </button>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (selectpg) {
                                const visibleIds = prod
                                  .filter((p) => p.producer_name.toLowerCase().includes(modalSearchQuery.toLowerCase()))
                                  .map((p) => Number(p.producer_id));
                                setSelectedProducers((prev) => Array.from(new Set([...prev, ...visibleIds])));
                              } else {
                                const visibleIds = Rep
                                  .filter((g) => g.producer_group_name.toLowerCase().includes(modalSearchQuery.toLowerCase()))
                                  .map((g) => Number(g.producer_group_id));
                                setSelectedProducerGroups((prev) => Array.from(new Set([...prev, ...visibleIds])));
                              }
                            }}
                            className="text-[#6755E8] hover:underline font-medium cursor-pointer"
                          >
                            Selectează tot
                          </button>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => {
                              if (selectpg) {
                                const visibleIds = new Set(
                                  prod
                                    .filter((p) => p.producer_name.toLowerCase().includes(modalSearchQuery.toLowerCase()))
                                    .map((p) => Number(p.producer_id))
                                );
                                setSelectedProducers((prev) => prev.filter((id) => !visibleIds.has(id)));
                              } else {
                                const visibleIds = new Set(
                                  Rep
                                    .filter((g) => g.producer_group_name.toLowerCase().includes(modalSearchQuery.toLowerCase()))
                                    .map((g) => Number(g.producer_group_id))
                                );
                                setSelectedProducerGroups((prev) => prev.filter((id) => !visibleIds.has(id)));
                              }
                            }}
                            className="text-rose-600 hover:underline font-medium cursor-pointer"
                          >
                            Deselectează tot
                          </button>
                        </div>
                      </div>

                      {/* List */}
                      <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50 rounded-xl border border-[#EEF1F6] dark:border-gray-700/50 p-1">
                        {selectpg
                          ? prod
                              .filter((p) => {
                                const matchName = p.producer_name.toLowerCase().includes(modalSearchQuery.toLowerCase());
                                if (!matchName) return false;
                                const isChecked = selectedProducers.includes(Number(p.producer_id));
                                if (modalFilterType === 'selected') return isChecked;
                                if (modalFilterType === 'unselected') return !isChecked;
                                return true;
                              })
                              .map((p) => {
                                const pid = Number(p.producer_id);
                                const isChecked = selectedProducers.includes(pid);
                                const wasInitial = initialAssignedProducers.includes(pid);
                                const isNewlyAdded = isChecked && !wasInitial;
                                const isBeingRemoved = !isChecked && wasInitial;

                                return (
                                  <label
                                    key={pid}
                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                      isBeingRemoved
                                        ? "bg-rose-50/50 dark:bg-rose-950/20"
                                        : isNewlyAdded
                                        ? "bg-indigo-50/50 dark:bg-indigo-950/20"
                                        : "hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded text-[#6755E8] focus:ring-[#6755E8] border-gray-300 cursor-pointer"
                                      checked={isChecked}
                                      onChange={() => handleToggleprod(pid, 'producer')}
                                    />
                                    <span className="text-xs font-medium text-[#1E293B] dark:text-gray-200 truncate">
                                      {p.producer_name}
                                    </span>
                                    {isNewlyAdded && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shrink-0">
                                        + Adăugare
                                      </span>
                                    )}
                                    {isBeingRemoved && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800 shrink-0">
                                        - Se va șterge
                                      </span>
                                    )}
                                    {wasInitial && isChecked && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 shrink-0">
                                        Asignat
                                      </span>
                                    )}
                                  </label>
                                );
                              })
                          : Rep
                              .filter((g) => {
                                const matchName = g.producer_group_name.toLowerCase().includes(modalSearchQuery.toLowerCase());
                                if (!matchName) return false;
                                const isChecked = selectedProducerGroups.includes(Number(g.producer_group_id));
                                if (modalFilterType === 'selected') return isChecked;
                                if (modalFilterType === 'unselected') return !isChecked;
                                return true;
                              })
                              .map((g) => {
                                const gid = Number(g.producer_group_id);
                                const isChecked = selectedProducerGroups.includes(gid);
                                const wasInitial = initialAssignedGroups.includes(gid);
                                const isNewlyAdded = isChecked && !wasInitial;
                                const isBeingRemoved = !isChecked && wasInitial;

                                return (
                                  <label
                                    key={gid}
                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                      isBeingRemoved
                                        ? "bg-rose-50/50 dark:bg-rose-950/20"
                                        : isNewlyAdded
                                        ? "bg-indigo-50/50 dark:bg-indigo-950/20"
                                        : "hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded text-[#6755E8] focus:ring-[#6755E8] border-gray-300 cursor-pointer"
                                      checked={isChecked}
                                      onChange={() => handleToggleprod(gid, 'group')}
                                    />
                                    <span className="text-xs font-medium text-[#1E293B] dark:text-gray-200 truncate">
                                      {g.producer_group_name}
                                    </span>
                                    {isNewlyAdded && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shrink-0">
                                        + Adăugare
                                      </span>
                                    )}
                                    {isBeingRemoved && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800 shrink-0">
                                        - Se va șterge
                                      </span>
                                    )}
                                    {wasInitial && isChecked && (
                                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 shrink-0">
                                        Asignat
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                      </div>

                      <div className="mt-5 flex items-center justify-between gap-3">
                        <div className="text-xs">
                          {hasModalChanges ? (
                            <div className="flex items-center gap-2">
                              {toAddProducers.length + toAddGroups.length > 0 && (
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  +{toAddProducers.length + toAddGroups.length} adăugări
                                </span>
                              )}
                              {toDeleteProducers.length + toDeleteGroups.length > 0 && (
                                <span className="font-semibold text-rose-600 dark:text-rose-400">
                                  -{toDeleteProducers.length + toDeleteGroups.length} ștergeri
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-[11px]">Nicio modificare nesalvată</span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-3.5 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                            onClick={() => setOpen(false)}
                          >
                            Anulează
                          </button>
                          <button
                            type="button"
                            disabled={isLoading || !hasModalChanges}
                            className="px-4 py-1.5 text-xs font-semibold bg-[#6755E8] hover:bg-[#5844E3] text-white rounded-lg shadow-xs disabled:opacity-50 transition-all cursor-pointer inline-flex items-center gap-1.5"
                            onClick={handleButtonClick}
                          >
                            {isLoading && (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            <span>{isLoading ? "Se salvează..." : "Salvează"}</span>
                          </button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>

          {/* Table */}
          {manageprod.length > 0 ? (
            <div>
              <div className="overflow-x-auto">
                <table className="table-auto w-full text-left">
                  <thead>
                    <tr className="bg-[#F8FAFC] dark:bg-gray-900/40 text-[#64748B] dark:text-gray-400 text-[11px] font-semibold uppercase tracking-wider h-10 border-b border-[#F1F5F9] dark:border-gray-700/60">
                      <th className="px-6 py-2.5">Denumirea</th>
                      <th className="px-6 py-2.5">Tip Producător</th>
                      <th className="px-6 py-2.5 text-center">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9] dark:divide-gray-700/40">
                    {currentItems.map((item) => (
                      <tr
                        key={item.id + "-" + item.type_id}
                        className="hover:bg-[#F8FAFC] dark:hover:bg-gray-800/60 transition-colors"
                      >
                        <td className="px-6 py-3.5 whitespace-nowrap text-[13px] font-medium text-[#1E293B] dark:text-gray-100">
                          <div className="flex items-center gap-2.5">
                            <div className="w-[28px] h-[28px] rounded-[7px] bg-[#F1EEFF] text-[#6755E8] flex items-center justify-center shrink-0">
                              <HiOutlineCube className="w-4 h-4" />
                            </div>
                            <span>{item.type_id === 1 ? item.producer_name : item.producer_group_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-[6px] text-[11.5px] font-medium ${
                            item.type_id === 1
                              ? "bg-[#F1EEFF] text-[#6755E8] dark:bg-indigo-950/60 dark:text-indigo-300"
                              : "bg-[#EBF5FF] text-[#1E429F] dark:bg-blue-950/60 dark:text-blue-300"
                          }`}>
                            {item.type_id === 1 ? "Producător individual" : "Grup producători"}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 whitespace-nowrap text-center">
                          {isAdmin ? (
                            <button
                              type="button"
                              disabled={deletingKey === `${item.id}-${item.type_id}`}
                              onClick={() => handleDelete(item)}
                              className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors cursor-pointer disabled:opacity-50"
                              title="Șterge accesul producătorului"
                            >
                              {deletingKey === `${item.id}-${item.type_id}` ? (
                                <div className="w-3.5 h-3.5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <HiOutlineTrash className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div className="px-6 py-3.5 border-t border-[#F1F5F9] dark:border-gray-700/60 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs font-medium text-[#64748B] dark:text-gray-400">
                  Total: {filteredManageProd.length} {filteredManageProd.length === 1 ? "înregistrare" : "înregistrări"}
                </span>

                <div className="flex items-center bg-[#F8FAFC] dark:bg-gray-800 p-1 rounded-lg border border-[#E2E8F0] dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 disabled:opacity-30 transition-colors cursor-pointer"
                  >
                    <HiChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    const isActive = page === currentPage;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          type="button"
                          onClick={() => setCurrentPage(page)}
                          className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium transition-colors cursor-pointer ${
                            isActive
                              ? "bg-[#1E293B] text-white shadow-xs"
                              : "text-[#64748B] hover:text-[#1E293B] hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                    if (
                      (page === 2 && currentPage > 3) ||
                      (page === totalPages - 1 && currentPage < totalPages - 2)
                    ) {
                      return (
                        <span key={page} className="px-1 text-gray-400 text-xs">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 disabled:opacity-30 transition-colors cursor-pointer"
                  >
                    <HiChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#64748B] dark:text-gray-400">Arată:</span>
                  <select
                    className="h-[34px] min-w-[130px] rounded-lg border border-[#E2E8F0] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700 text-xs font-medium text-[#334155] dark:text-gray-200 pl-3 pr-8 py-1 focus:outline-none focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 transition-all cursor-pointer"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5 pe pagină</option>
                    <option value={10}>10 pe pagină</option>
                    <option value={15}>15 pe pagină</option>
                    <option value={manageprod.length || 50}>Toate</option>
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-14 px-6 text-center flex flex-col items-center justify-center min-h-[220px]">
              <div className="w-[52px] h-[52px] rounded-[14px] bg-[#F6F5FF] dark:bg-indigo-950/40 text-[#7B6CF0] dark:text-indigo-400 flex items-center justify-center mb-3 shadow-[0_2px_8px_rgba(103,85,232,0.06)]">
                <HiOutlineBuildingOffice2 className="w-6 h-6" />
              </div>
              <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">
                Niciun acces la producători
              </p>
              <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 max-w-xs leading-relaxed">
                Nu există producători sau grupuri de producători asociați acestui cont.
              </p>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleOpenProd}
                  className="mt-4 h-[34px] px-3.5 rounded-[8px] bg-[#6755E8] hover:bg-[#5844E3] text-white text-[11px] font-medium inline-flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                >
                  <HiPlus className="w-3.5 h-3.5" />
                  <span>Adaugă producător</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DashboardCard07Prod;